import { useRef } from 'react'
import type { Asset, Segment, Section } from '@shared/types'
import { COLOR_GRADES } from '@shared/colorGrades'
import { Gauge } from 'lucide-react'
import { api } from '../../../api'
import { useEditorStore } from '../../../store/editorStore'
import { useUiStore } from '../../../store/uiStore'
import { assetDisplayName } from '../../../lib/assetName'
import {
  collectSegmentTrimSnapTargets,
  findSceneForSegment,
  getSegmentTimeline,
  secToPx,
  segmentOutputDuration,
  snapSec,
} from '../../../lib/timelineMath'
import { SEGMENT_REORDER_MIME, SNAP_TOLERANCE_PX, VIDEO_LANE_H } from './constants'

/** task spec §2-10: asset 名は「末尾12文字程度に省略」。 */
function tailName(name: string, n = 12): string {
  return name.length > n ? `…${name.slice(-n)}` : name
}

export function SegmentBlock({
  segment,
  index,
  startSec,
  pxPerSec,
  asset,
  section,
  selected,
}: {
  segment: Segment
  index: number
  /** 合成後タイムライン上の開始秒（this Segment の左端）。 */
  startSec: number
  pxPerSec: number
  asset: Asset | undefined
  section: Section | undefined
  selected: boolean
}) {
  const select = useEditorStore((s) => s.select)
  const toggleSelection = useEditorStore((s) => s.toggleSelection)
  const updateSegment = useEditorStore((s) => s.updateSegment)
  const beginHistoryBatch = useEditorStore((s) => s.beginHistoryBatch)
  const endHistoryBatch = useEditorStore((s) => s.endHistoryBatch)
  const snapEnabled = useEditorStore((s) => s.snapEnabled)
  const setSnapGuideSec = useEditorStore((s) => s.setSnapGuideSec)
  const hoverAssetId = useUiStore((s) => s.hoverAssetId)

  const outputDur = segmentOutputDuration(segment)
  const width = Math.max(6, outputDur * pxPerSec)
  const left = secToPx(startSec, pxPerSec)
  const draggingEdge = useRef<'left' | 'right' | null>(null)
  const dragStart = useRef({
    x: 0,
    inSec: 0,
    outSec: 0,
    timelineStartSec: 0,
    timelineEndSec: 0,
    // reviewer 差し戻し B1: ドラッグ開始時点で一度だけ計算して凍結した吸着候補。
    // pointermove の間はこれを使い回し、ライブな Composition から毎フレーム
    // 取り直さない（取り直すと次 Segment の startSec が「いま動かしている端
    // そのもの」と一致し続け、距離 0 で自分自身に吸着し続けて 8px 刻みでしか
    // 動かせなくなるバグの原因だった）。
    snapTargets: [] as number[],
  })

  function beginResize(edge: 'left' | 'right', e: React.PointerEvent) {
    e.stopPropagation()
    e.preventDefault()
    draggingEdge.current = edge
    const timelineStartSec = startSec
    const timelineEndSec = startSec + outputDur
    let snapTargets: number[] = []
    const store = useEditorStore.getState()
    const comp = store.composition
    if (comp) {
      const timeline = getSegmentTimeline(comp.tracks.video)
      snapTargets = collectSegmentTrimSnapTargets(timeline, comp.tracks.overlays, store.playback.currentSec, {
        segmentIndex: index,
        excludeCoincidentSec: edge === 'left' ? timelineStartSec : timelineEndSec,
      })
    }
    dragStart.current = {
      x: e.clientX,
      inSec: segment.inSec,
      outSec: segment.outSec,
      timelineStartSec,
      timelineEndSec,
      snapTargets,
    }
    beginHistoryBatch()
    ;(e.target as HTMLElement).setPointerCapture(e.pointerId)
  }

  function onResizeMove(e: React.PointerEvent) {
    if (!draggingEdge.current) return
    const deltaPx = e.clientX - dragStart.current.x
    let deltaSourceSec = (deltaPx / pxPerSec) * segment.speed
    const maxOut = asset?.durationSec ?? Number.POSITIVE_INFINITY

    // 吸着: トリムでできる新しい「合成タイムライン上の端」を、beginResize で
    // 凍結済みの候補へ寄せる（ここでは絶対に collectSnapTargets を取り直さない）。
    if (snapEnabled) {
      const targets = dragStart.current.snapTargets
      const toleranceSec = SNAP_TOLERANCE_PX / pxPerSec
      if (draggingEdge.current === 'left') {
        const rawEdgeSec = dragStart.current.timelineStartSec + deltaPx / pxPerSec
        const snapped = snapSec(rawEdgeSec, targets, toleranceSec)
        deltaSourceSec = (snapped.sec - dragStart.current.timelineStartSec) * segment.speed
        setSnapGuideSec(snapped.snappedTo)
      } else {
        const rawEdgeSec = dragStart.current.timelineEndSec + deltaPx / pxPerSec
        const snapped = snapSec(rawEdgeSec, targets, toleranceSec)
        deltaSourceSec = (snapped.sec - dragStart.current.timelineEndSec) * segment.speed
        setSnapGuideSec(snapped.snappedTo)
      }
    }

    if (draggingEdge.current === 'left') {
      const nextIn = Number(
        Math.min(dragStart.current.outSec - 0.2, Math.max(0, dragStart.current.inSec + deltaSourceSec)).toFixed(2),
      )
      // reviewer 差し戻し m2: サブピクセルのジッターで値が変わらないなら
      // mutate を呼ばない（無駄な再レンダー/save再スケジュールを避ける）。
      if (nextIn !== segment.inSec) updateSegment(segment.id, { inSec: nextIn })
    } else {
      const nextOut = Number(
        Math.max(dragStart.current.inSec + 0.2, Math.min(maxOut, dragStart.current.outSec + deltaSourceSec)).toFixed(
          2,
        ),
      )
      if (nextOut !== segment.outSec) updateSegment(segment.id, { outSec: nextOut })
    }
  }

  function endResize(e: React.PointerEvent) {
    if (draggingEdge.current) {
      ;(e.target as HTMLElement).releasePointerCapture(e.pointerId)
      endHistoryBatch()
      setSnapGuideSec(null)
    }
    draggingEdge.current = null
  }

  const swatch = section ? COLOR_GRADES[section.color].swatch : '#3A4356'
  const scene = findSceneForSegment(asset, segment)
  const thumbPath = scene?.thumbPath ?? asset?.thumbPath ?? null
  const thumbW = Math.min(40, width)
  // task spec §2-10: hoverAssetId と一致する asset の Segment は水色で淡くハイライト
  // （選択中の白枠より優先度は下 — 両方成立する時は選択の見た目を優先する）。
  const isHoverHighlight = !selected && !!hoverAssetId && hoverAssetId === segment.assetId

  return (
    <div
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData(SEGMENT_REORDER_MIME, String(index))
        e.dataTransfer.effectAllowed = 'move'
      }}
      onClick={(e) => {
        e.stopPropagation()
        if (e.shiftKey) toggleSelection({ kind: 'segment', id: segment.id })
        else select({ kind: 'segment', id: segment.id })
      }}
      data-testid="segment-block"
      data-segment-id={segment.id}
      className={`absolute top-1 rounded overflow-hidden cursor-grab active:cursor-grabbing border-2 transition-shadow ${
        selected
          ? 'border-white shadow-glow'
          : isHoverHighlight
            ? 'border-accent-blue/70'
            : 'border-transparent hover:border-white/30'
      }`}
      style={{ left, width, height: VIDEO_LANE_H - 8, backgroundColor: swatch + '33' }}
    >
      {thumbPath && (
        <div className="absolute inset-y-0 left-0 overflow-hidden" style={{ width: thumbW }}>
          <img
            src={api.fileUrl(thumbPath)}
            alt=""
            className="w-full h-full object-cover opacity-80 pointer-events-none"
          />
        </div>
      )}
      <div className="absolute inset-x-0 top-0 h-[3px]" style={{ backgroundColor: swatch }} />
      {segment.speed !== 1 && (
        <span className="absolute top-1 right-1 flex items-center gap-0.5 shrink-0 bg-black/60 rounded px-1 text-[9px] text-white">
          <Gauge size={9} />
          {segment.speed}x
        </span>
      )}
      <div
        className="absolute bottom-0.5 left-1 right-1 flex items-center justify-between gap-1 text-[9px] text-white drop-shadow"
        style={{ paddingLeft: thumbPath ? Math.max(0, thumbW - 4) : 0 }}
      >
        <span className="truncate">
          {tailName(asset ? assetDisplayName(asset) : '?')}
          {scene && <span className="text-white/70"> #{scene.index}</span>}
        </span>
        <span className="shrink-0 tabular-nums">{outputDur.toFixed(1)}s</span>
      </div>
      {/* trim handles */}
      <div
        draggable={false}
        onPointerDown={(e) => beginResize('left', e)}
        onPointerMove={onResizeMove}
        onPointerUp={endResize}
        onPointerCancel={endResize}
        className="absolute left-0 top-0 bottom-0 w-2 cursor-col-resize hover:bg-white/30"
        data-testid="trim-handle-left"
      />
      <div
        draggable={false}
        onPointerDown={(e) => beginResize('right', e)}
        onPointerMove={onResizeMove}
        onPointerUp={endResize}
        onPointerCancel={endResize}
        className="absolute right-0 top-0 bottom-0 w-2 cursor-col-resize hover:bg-white/30"
        data-testid="trim-handle-right"
      />
    </div>
  )
}
